import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, SegmentedTabs } from "@akuna/ui";
import { LoadingSkeleton as Skeleton } from "@/components/ui/loading-skeleton";
import { Badge } from "@/components/ui/badge";
import { useHospitalization } from "@/hooks/useHospitalization";
import { EvolucaoTab } from "@/components/hospitalization/tabs/EvolucaoTab";
import { MedicamentosTab } from "@/components/hospitalization/tabs/MedicamentosTab";
import { DietaTab } from "@/components/hospitalization/tabs/DietaTab";
import { ExamesTab } from "@/components/hospitalization/tabs/ExamesTab";
import { FinanceiroTab } from "@/components/hospitalization/tabs/FinanceiroTab";
import { DischargeDialog } from "@/components/hospitalization/DischargeDialog";
import { ArrowLeft, LogOut, BedDouble, Stethoscope } from "lucide-react";
import { formatDistanceToNow, format, differenceInDays } from "date-fns";
import { ptBR } from "date-fns/locale";

const AVATAR_COLORS = [
  "from-orange-200 to-amber-300 border-orange-200 text-orange-900",
  "from-purple-200 to-purple-300 border-purple-200 text-purple-900",
  "from-blue-200 to-blue-300 border-blue-200 text-blue-900",
  "from-emerald-200 to-emerald-300 border-emerald-200 text-emerald-900",
  "from-rose-200 to-rose-300 border-rose-200 text-rose-900",
];

function petAvatarColor(name: string) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffff;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

type HospitalizationStatus = "ativa" | "alta" | "obito";

function StatusBadge({ status }: { status: string }) {
  if (status === "ativa")
    return <Badge className="bg-primary/10 text-primary border-0 font-semibold">Internado</Badge>;
  if (status === "obito")
    return <Badge variant="destructive" className="font-semibold">Óbito</Badge>;
  return <Badge variant="secondary" className="font-semibold">Alta</Badge>;
}

const TABS = [
  "evolucao",
  "medicamentos",
  "exames",
  "dieta",
  "financeiro",
] as const;

export default function HospitalizationRecord() {
  const { id, tab } = useParams<{ id: string; tab?: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("hospitalization");
  const { data: hospitalization, isLoading, isError } = useHospitalization(id);
  const [showAlta, setShowAlta] = useState(false);
  const locale = i18n.language === "pt" ? ptBR : undefined;

  const activeTab = (TABS as readonly string[]).includes(tab ?? "") ? tab! : TABS[0];

  function handleTabChange(value: string) {
    navigate(`/hospitalization/${id}/${value}`);
  }

  if (isLoading) {
    return (
      <div className="p-4 md:p-6 space-y-4 max-w-5xl mx-auto">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-32 w-full rounded-2xl" />
        <Skeleton className="h-16 w-full rounded-2xl" />
        <Skeleton className="h-64 w-full rounded-2xl" />
      </div>
    );
  }

  if (isError || !hospitalization) {
    return (
      <div className="p-4 text-center text-muted-foreground py-16">
        <p>{t("errors.loadFailed")}</p>
        <Button asChild variant="link" className="mt-2">
          <Link to="/hospitalization">{t("dashboard.title")}</Link>
        </Button>
      </div>
    );
  }

  const pet = hospitalization.pets;
  const baia = hospitalization.hospitalization_bays;
  const customerId = hospitalization.customer_id ?? "";
  const baiaLabel = baia
    ? `${baia.hospitalization_sectors?.nome ?? ""} · ${baia.nome}`
    : t("ficha.semBaia");
  const diasInternado = differenceInDays(
    hospitalization.alta_at ? new Date(hospitalization.alta_at) : new Date(),
    new Date(hospitalization.entrada_at)
  );
  const avatarColor = petAvatarColor(pet?.name ?? "?");
  const initial = (pet?.name ?? "?").charAt(0).toUpperCase();

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-5xl mx-auto">
      {/* Top bar */}
      <div className="flex items-center justify-between gap-2">
        <Button asChild variant="ghost" size="sm" className="text-muted-foreground">
          <Link to="/hospitalization">
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            {t("dashboard.title")}
          </Link>
        </Button>
        {hospitalization.status === "ativa" && (
          <Button variant="outline" size="sm" onClick={() => setShowAlta(true)}>
            <LogOut className="h-4 w-4 mr-1.5" />
            {t("alta.title")}
          </Button>
        )}
      </div>

      {/* Hero header */}
      <div className="rounded-2xl border border-border bg-card overflow-hidden shadow-sm">
        <div className="px-5 pt-5 pb-4 flex items-start gap-4">
          {/* Avatar */}
          <div className={`w-16 h-16 rounded-2xl flex-shrink-0 flex items-center justify-center bg-gradient-to-br border text-2xl font-bold ${avatarColor}`}>
            {initial}
          </div>

          {/* Name + meta */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold tracking-tight leading-none">{pet?.name}</h1>
              <StatusBadge status={hospitalization.status} />
              <span className="font-mono text-[11px] text-muted-foreground bg-muted px-2 py-0.5 rounded ml-auto">
                #{id?.slice(0, 8).toUpperCase()}
              </span>
            </div>
            <div className="flex items-center gap-3 flex-wrap mt-2 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <BedDouble className="h-3.5 w-3.5" />
                {baiaLabel}
              </span>
              <span className="text-border">·</span>
              <span>
                {t("ficha.internadoHa", {
                  tempo: formatDistanceToNow(new Date(hospitalization.entrada_at), {
                    locale,
                    addSuffix: false,
                  }),
                })}
              </span>
            </div>
          </div>
        </div>

        {/* Status strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 border-t border-border">
          {[
            {
              label: t("ficha.entrada"),
              value: format(new Date(hospitalization.entrada_at), "dd/MM/yyyy", { locale }),
              sub: format(new Date(hospitalization.entrada_at), "HH:mm", { locale }),
            },
            {
              label: t("ficha.diasInternado"),
              value: diasInternado === 0 ? t("ficha.hoje") : `${diasInternado}d`,
              sub: diasInternado === 0 ? "" : diasInternado === 1 ? t("ficha.umDia") : "",
            },
            {
              label: t("ficha.baia_label"),
              value: baia?.nome ?? "—",
              sub: baia?.hospitalization_sectors?.nome ?? "",
            },
            {
              label: t("ficha.previsaoAlta"),
              value: hospitalization.previsao_alta_at
                ? format(new Date(hospitalization.previsao_alta_at), "dd/MM/yyyy", { locale })
                : "—",
              sub: hospitalization.alta_at
                ? format(new Date(hospitalization.alta_at), "dd/MM/yyyy", { locale })
                : "",
              highlight: !!hospitalization.alta_at,
            },
          ].map((item, i) => (
            <div
              key={i}
              className={`px-4 py-3 flex flex-col gap-0.5 ${i < 3 ? "border-r border-border" : ""}`}
            >
              <span className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
                {item.label}
              </span>
              <span className={`text-sm font-bold tracking-tight ${item.highlight ? "text-emerald-600" : "text-foreground"}`}>
                {item.value}
              </span>
              {item.sub && (
                <span className="text-[11px] text-muted-foreground">{item.sub}</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Context cards: reason + diagnosis */}
      {(hospitalization.motivo || hospitalization.diagnostico_inicial) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {hospitalization.motivo && (
            <div className="rounded-2xl border border-primary/20 bg-primary/5 p-4 flex gap-3 items-start">
              <div className="w-9 h-9 rounded-xl flex-shrink-0 flex items-center justify-center bg-primary/15 text-primary">
                <LogOut className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground mb-1">
                  {t("ficha.motivo")}
                </p>
                <p className="text-sm font-semibold leading-snug text-foreground">
                  {hospitalization.motivo}
                </p>
              </div>
            </div>
          )}
          {hospitalization.diagnostico_inicial && (
            <div className="rounded-2xl border border-blue-200 bg-blue-50/50 dark:border-blue-900/40 dark:bg-blue-950/20 p-4 flex gap-3 items-start">
              <div className="w-9 h-9 rounded-xl flex-shrink-0 flex items-center justify-center bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400">
                <Stethoscope className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground mb-1">
                  {t("ficha.diagnostico")}
                </p>
                <p className="text-sm font-semibold leading-snug text-foreground">
                  {hospitalization.diagnostico_inicial}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <SegmentedTabs
        value={activeTab}
        onValueChange={handleTabChange}
        fill="screen"
        items={[
          {
            value: "evolucao",
            label: t("ficha.tabs.evolucao"),
            content: (
              <EvolucaoTab
                hospitalizationId={hospitalization.id}
                customerId={customerId}
                evolucoes={hospitalization.hospitalization_evolutions ?? []}
              />
            ),
          },
          {
            value: "medicamentos",
            label: t("ficha.tabs.medicamentos"),
            content: (
              <MedicamentosTab
                hospitalizationId={hospitalization.id}
                customerId={customerId}
                prescricoes={hospitalization.hospitalization_prescriptions ?? []}
              />
            ),
          },
          {
            value: "exames",
            label: t("ficha.tabs.exames"),
            content: (
              <ExamesTab
                hospitalizationId={hospitalization.id}
                customerId={customerId}
                exames={hospitalization.hospitalization_exams ?? []}
              />
            ),
          },
          {
            value: "dieta",
            label: t("ficha.tabs.dieta"),
            content: (
              <DietaTab
                hospitalizationId={hospitalization.id}
                customerId={customerId}
                dieta={hospitalization.hospitalization_diet ?? []}
              />
            ),
          },
          {
            value: "financeiro",
            label: t("ficha.tabs.financeiro"),
            content: (
              <FinanceiroTab
                hospitalizationId={hospitalization.id}
                customerId={customerId}
                itens={hospitalization.hospitalization_finance ?? []}
              />
            ),
          },
        ]}
      />

      {hospitalization.status === "ativa" && (
        <DischargeDialog
          open={showAlta}
          onOpenChange={setShowAlta}
          hospitalizationId={hospitalization.id}
          petNome={pet?.name ?? ""}
        />
      )}
    </div>
  );
}
